import React, { useState, useCallback } from 'react';
import { Printer, Search, Truck } from 'lucide-react';
import apiClient from '../../utils/api';

export const DispatchManifest: React.FC = () => {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [transporterId, setTransporterId] = useState('');
  const [transporters, setTransporters] = useState<any[]>([]);
  const [shipments, setShipments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  React.useEffect(() => {
    apiClient.listTransporters().then(r => {
      const d = r.data.data;
      setTransporters(d?.users ?? d?.rows ?? (Array.isArray(d) ? d : []));
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { date };
      if (transporterId) params.transporterId = transporterId;
      const res = await apiClient.getManifest(params);
      const d = res.data.data;
      setShipments(Array.isArray(d) ? d : []);
      setSearched(true);
    } catch {} finally { setLoading(false); }
  }, [date, transporterId]);

  const handlePrint = () => window.print();

  const totalAmount = shipments.reduce((s, sh) => s + Number(sh.totalAmount || 0), 0);
  const totalItems = shipments.reduce((s, sh) => s + (sh.items?.reduce((a: number, i: any) => a + i.quantity, 0) || 0), 0);

  return (
    <>
      <div className="page-header no-print">
        <h1 className="page-title">Dispatch Manifest</h1>
        {shipments.length > 0 && (
          <button className="btn btn-ghost" onClick={handlePrint}><Printer size={16} /> Print</button>
        )}
      </div>

      <div className="card no-print" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 160 }}>
            <label>Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0, flex: 2, minWidth: 200 }}>
            <label>Transporter (optional)</label>
            <select value={transporterId} onChange={e => setTransporterId(e.target.value)}>
              <option value="">— All Transporters —</option>
              {transporters.map(t => (
                <option key={t.id} value={t.id}>{t.firstName} {t.lastName} {t.phone ? `(${t.phone})` : ''}</option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary" onClick={load} disabled={loading}>
            <Search size={15} /> {loading ? 'Loading...' : 'Generate'}
          </button>
        </div>
      </div>

      {searched && (
        <div className="card print-area">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Trip Manifest</h2>
              <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>
                Date: {new Date(date).toLocaleDateString('en-RW', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                {transporterId && transporters.find(t => t.id === transporterId) && (
                  <> &nbsp;·&nbsp; Transporter: {transporters.find(t => t.id === transporterId)?.firstName} {transporters.find(t => t.id === transporterId)?.lastName}</>
                )}
              </p>
            </div>
            <div style={{ textAlign: 'right', fontSize: 13, color: '#64748b' }}>
              <div>Total Shipments: <strong>{shipments.length}</strong></div>
              <div>Total Items: <strong>{totalItems}</strong></div>
              <div>Total Value: <strong>{totalAmount.toLocaleString()} RWF</strong></div>
            </div>
          </div>

          {shipments.length === 0 ? (
            <div className="empty-state"><h3>No dispatched shipments found for this date</h3></div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Tracking #</th>
                    <th>Receiver</th>
                    <th>Destination</th>
                    <th>Phone</th>
                    <th>Items</th>
                    <th>Amount (RWF)</th>
                    <th>Transporter</th>
                    <th>Vehicle</th>
                    <th>Status</th>
                    <th className="no-print">Signature</th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s: any, i: number) => (
                    <tr key={s.id}>
                      <td style={{ fontSize: 12, color: '#94a3b8' }}>{i + 1}</td>
                      <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                      <td style={{ fontSize: 13 }}>{s.receiverName}</td>
                      <td style={{ fontSize: 13 }}>{s.receiverCity || s.receiverAddress}</td>
                      <td style={{ fontSize: 13 }}>{s.receiverPhone}</td>
                      <td style={{ fontSize: 13 }}>{s.items?.reduce((a: number, it: any) => a + it.quantity, 0) || 0}</td>
                      <td style={{ fontSize: 13 }}>{Number(s.totalAmount).toLocaleString()}</td>
                      <td style={{ fontSize: 13 }}>{s.transporter ? `${s.transporter.firstName} ${s.transporter.lastName}` : '—'}</td>
                      <td style={{ fontSize: 13 }}>{s.vehicleId || '—'}</td>
                      <td><span className={`badge badge-${s.status?.toLowerCase().replace(/_/g, '-')}`}>{s.status}</span></td>
                      <td className="no-print" style={{ minWidth: 100, borderBottom: '1px solid #e2e8f0' }}></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 600, background: '#f8fafc' }}>
                    <td colSpan={6} style={{ textAlign: 'right', paddingRight: 12, fontSize: 13 }}>Total</td>
                    <td style={{ fontSize: 13 }}>{totalAmount.toLocaleString()}</td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div style={{ marginTop: 40, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 40 }} className="print-signatures">
            {['Prepared By', 'Approved By', 'Transporter Signature'].map(label => (
              <div key={label} style={{ borderTop: '1px solid #334155', paddingTop: 8, fontSize: 12, color: '#64748b' }}>{label}</div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .sidebar, .layout-header { display: none !important; }
          .main-content { margin: 0 !important; padding: 0 !important; }
          .print-area { box-shadow: none !important; border: none !important; }
        }
      `}</style>
    </>
  );
};
